/**
 * Copy Revised Bar Component
 *
 * Sticky bar to copy the revised message with accepted suggestions.
 */

import React, { useCallback, useState, useRef, useEffect } from 'react';
import { View, Text, Pressable, Platform } from 'react-native';
import * as Clipboard from 'expo-clipboard';
import { useTranslation } from '@dramadetector/shared';
import { captureEvent } from '@/lib/analytics';
import type { CopyRevisedBarProps } from './types';
import { styles } from './inlineAnnotationsStyles';

/**
 * CopyRevisedBar - Copy revised message button
 */
export function CopyRevisedBar({
  revisedText,
  acceptedCount,
  totalCount,
  hasChanges,
  testID,
}: CopyRevisedBarProps) {
  const { t } = useTranslation('features');
  const [copied, setCopied] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Cleanup timeout on unmount to prevent memory leak
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const handleCopy = useCallback(async () => {
    if (!hasChanges) return;

    try {
      // Clear any existing timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      await Clipboard.setStringAsync(revisedText);
      setCopied(true);

      // Track analytics
      captureEvent('InlineAnnotation_CopyRevised', {
        accepted_count: acceptedCount,
        total_count: totalCount,
      });

      // Reset copied state after 2 seconds (with cleanup ref)
      timeoutRef.current = setTimeout(() => {
        setCopied(false);
        timeoutRef.current = null;
      }, 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  }, [revisedText, hasChanges, acceptedCount, totalCount]);

  // Don't show if no changes
  if (!hasChanges) {
    return null;
  }

  return (
    <View style={styles.copyBar} testID={testID}>
      <Text style={styles.copyBarText}>
        {t('inline_annotations.suggestions_applied', '{{count}} suggestions applied', {
          count: acceptedCount,
        })}
      </Text>
      <Pressable
        style={[styles.copyButton, !hasChanges && styles.copyButtonDisabled]}
        onPress={handleCopy}
        disabled={!hasChanges}
        accessibilityRole="button"
        accessibilityLabel={t('inline_annotations.copy_revised', 'Copy revised message')}
        testID={`${testID}-button`}
      >
        <Text style={styles.copyButtonText}>
          {copied
            ? t('inline_annotations.copied', 'Copied!')
            : t('inline_annotations.copy', 'Copy')}
        </Text>
      </Pressable>
    </View>
  );
}

export default CopyRevisedBar;
